import { Tooltip } from '@mui/material';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import Select from 'react-select';
import * as yup from 'yup';
import Breadcrumb from '../components/Common/Breadcrumb';
import DeleteFacilityModal from '../components/Facilities/DeleteFacilityModal';
import FacilityMetrics from '../components/Facilities/FacilityMetrics';
import Modal from '../components/Facilities/Modal';
import { capitalizeFirstLetter, itemToOptions } from '../components/utils/GlobalFunctions';
import { flexBetweenCenter, selectStyles } from '../components/utils/ReactSelectStyles';
import { Actions as FACILITYACTIONS } from '../store/actions/facilityActions';
import { CustomOption } from '../components/Common/ReusableComponents/Reusable';
import { TooltipText } from '../styledComponents/GlobalStyle';
import NumberFormat from 'react-number-format';

const Facility = ({
  facilities,
  saveFacility,
  showFacilityModal,
  facilityDataModal,
  facilityDataDeleteModal,
  branding,
  facilityOptions,
  selectedOptions,
  facilitiesPermissions,
}) => {
  const { id } = useParams();
  const { primaryColor, tooltipBackground, tooltipText } = branding?.colors;
  const [editing, setEditing] = useState(id === 'new');
  const [editedState, setEditedState] = useState(null);
  const [validationError, setValidationError] = useState({});
  const { headers } = branding.fonts?.[0].font;
  const history = useHistory();
  const facility = facilities?.find((item) => item.id === id);
  const checkingForPrimaryFacility = facilities?.some((facility) => facility?.isPrimary);
  const canEdit = (facilitiesPermissions?.[id]?.edit ?? facilitiesPermissions?.['*']?.edit) || false;

  let selectedItem = {};
  selectedOptions &&
    Object.keys(selectedOptions).map(
      (x) =>
        (selectedItem[x] =
          selectedOptions[x] &&
          selectedOptions[x].find((item) => {
            return editedState && editedState?.[x] && item.value === editedState?.[x];
          })),
    );

  const addFacilityValidation = yup.object().shape({
    name: yup.string().required('Company name is required'),
    facilityTypeId: yup.string().required('Facility Type Id is required'),
    isPrimary: yup.string().required('Last name is required'),
    country: yup.string().required('Country is required'),
    postCode: yup.string().required('Post Code or External Id are required'),
  });

  const onConfirm = () => {
    const primaryFacility = facilities.find((item) => item.isPrimary);
    saveFacility({ ...primaryFacility, isPrimary: false });
    saveFacility({ ...facility, ...editedState }, history);
  };

  useEffect(() => {
    if (id === 'new') {
      setEditing(true);
      setEditedState({
        name: '',
        postCode: '',
        externalId: '',
        facilityTypeId: null,
        isPrimary: false,
        country: null,
        excludeFromReports: false,
        facilityM2: '',
        address: '',
      });
    }
  }, [id]);

  const updateField = (e) => {
    const newEditedState = { ...editedState };
    newEditedState[e.target.name] = e.target.value;
    setEditedState(newEditedState);
  };

  if (!facility && id !== 'new') {
    return (
      <div className='row' style={{ fontFamily: headers }}>
        <div className='col-lg-12'>
          <section className='panel'>
            <header className='panel-heading'>Facility not Found</header>
          </section>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <Breadcrumb
        items={[
          { label: 'Facilities', link: '/facilities' },
          {
            label: facility ? facility.name : 'New',
            link: `/facilities/${facility ? facility.id : 'new'}`,
          },
        ]}
      />

      <div className='row' style={{ fontFamily: headers }}>
        <div className='col-lg-12'>
          <section className='panel'>
            <aside className='profile-nav alt'>
              <section className='panel' id='facilityDetails'>
                <header className='panel-heading' id='facilitiesHeading'>
                  Your facilities - {facility?.name || editedState?.name}
                  {editing ? (
                    <i
                      style={{ cursor: 'pointer', float: 'right', fontSize: 25 }}
                      className='far fa-save fa-lg'
                      id='save-icon'
                      onClick={async () => {
                        await addFacilityValidation
                          .validate(
                            {
                              name: editedState['name'],
                              facilityTypeId: editedState['facilityTypeId'],
                              isPrimary: editedState['isPrimary'],
                              country: editedState['country'],
                              postCode: editedState['postCode'] || editedState['externalId'],
                            },
                            { abortEarly: false },
                          )
                          .then(async () => {
                            try {
                              if (checkingForPrimaryFacility && editedState?.['isPrimary'] && !facility?.isPrimary)
                                showFacilityModal(true);
                              else {
                                await saveFacility({ ...facility, ...editedState }, history);
                                setEditing(false);
                                if (validationError) setValidationError({}); // Reset the State
                              }
                            } catch (error) {
                              console.log('error', error);
                            }
                          })
                          .catch((error) => {
                            setValidationError(error.inner);
                          });
                      }}
                    />
                  ) : (
                    canEdit && (
                      <i
                        style={{ cursor: 'pointer', float: 'right', fontSize: 25 }}
                        onClick={() => {
                          setEditing(true);
                          setEditedState({
                            name: facility.name,
                            postCode: facility.postCode,
                            externalId: facility.externalId,
                            facilityTypeId: facility.facilityTypeId,
                            isPrimary: facility.isPrimary,
                            country: facility.country,
                            excludeFromReports: facility.excludeFromReports,
                            facilityM2: facility.facilityM2,
                            address: facility.address,
                          });
                        }}
                        id='edit-facility'
                        className='fa fa-pencil fa-lg'
                      />
                    )
                  )}
                </header>
                <ul id='Facility-table' className='nav nav-pills nav-stacked'>
                  {Object.keys(facilityOptions).map((key) => {
                    const { type, name, placeholder } = facilityOptions[key] || {};
                    const { show: showTooltip, content } = facilityOptions[key]?.tooltip || {};
                    let value;

                    // Handle Logic to show values on the UI when not on editing mode
                    if (!editing) {
                      if (type === 'select') {
                        value = _.find(selectedOptions?.[key], { value: facility?.[key] })?.label || 'N/A';
                      } else if (type === 'text') {
                        value = capitalizeFirstLetter(facility?.[key]) || 'N/A';
                      } else if (type === 'number') {
                        value = facility?.[key] || 'N/A';
                      }
                    }
                    return (
                      <li className={key} key={key}>
                        <div
                          style={{
                            ...flexBetweenCenter,
                            border: _.find(validationError, { path: key })?.message ? '3px solid red' : 'none',
                            padding: '10px 15px',
                          }}>
                          <div style={{ minWidth: 150, display: 'flex', alignItems: 'center' }}>
                            <i className={facilityOptions[key].icon} style={{ paddingRight: 20 }} />
                            {facilityOptions[key].label}
                            {showTooltip && (
                              <Tooltip
                                title={<TooltipText>{content}</TooltipText>}
                                placement='right'
                                arrow
                                componentsProps={{
                                  tooltip: { sx: { bgcolor: tooltipBackground, color: tooltipText } },
                                  arrow: { sx: { color: tooltipBackground } },
                                }}>
                                <i
                                  className='fa fa-question-circle'
                                  style={{ fontSize: 18, color: primaryColor, marginLeft: 10 }}
                                />
                              </Tooltip>
                            )}
                          </div>
                          {editing ? (
                            <>
                              {type === 'text' && (
                                <input
                                  type='text'
                                  id={`Facility-${name}`}
                                  name={name}
                                  placeholder={placeholder}
                                  value={editedState?.[name] || ''}
                                  onChange={updateField}
                                  className='form_input'
                                />
                              )}
                              {type === 'number' && (
                                <NumberFormat
                                  inputMode='numeric'
                                  id={`Facility-${name}`}
                                  name={name}
                                  placeholder={placeholder}
                                  value={editedState?.[name] || ''}
                                  onChange={updateField}
                                  className='form_input'
                                  isAllowed={(values) => {
                                    const { floatValue } = values;
                                    return floatValue === undefined || floatValue < 1000000000000;
                                  }}
                                />
                              )}
                              {type === 'select' && (
                                <Select
                                  className={key}
                                  id={`Facility-${name}`}
                                  value={selectedItem?.[key] || value}
                                  options={facilityOptions?.[key].options}
                                  styles={selectStyles}
                                  isClearable
                                  menuPlacement={'auto'}
                                  components={{ Option: CustomOption('Facility') }}
                                  onChange={(selected) => {
                                    const newEditedState = { ...editedState };
                                    newEditedState[key] = selected ? selected?.value : null;
                                    setEditedState(newEditedState);
                                  }}
                                />
                              )}
                            </>
                          ) : (
                            <span style={{ float: 'right' }}>{value}</span>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            </aside>
          </section>

          {facility?.id && <FacilityMetrics id={id} facility={facility} />}
        </div>
      </div>

      {facilityDataModal && (
        <Modal facility={{ ...facility, ...editedState }} onConfirm={onConfirm} setEditing={setEditing} />
      )}
      {facilityDataDeleteModal?.status && <DeleteFacilityModal />}
    </React.Fragment>
  );
};

const mapStateToProps = (state) => {
  const facilityTypeOptions = _.sortBy(state?.facility?.types?.map(itemToOptions), ['label']);
  const countryOptions = _.sortBy(state.organisation?.countries?.map(itemToOptions), ['label']);
  const primaryFacilityOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ];

  const excludeFromReportsOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ];

  const selectedOptions = {
    facilityTypeId: facilityTypeOptions,
    country: countryOptions,
    isPrimary: primaryFacilityOptions,
    excludeFromReports: excludeFromReportsOptions,
  };

  const facilityOptions = {};
  facilityOptions.name = {
    label: 'Facility Name',
    name: 'name',
    type: 'text',
    icon: 'fa fa-cogs',
    placeholder: '',
  };
  facilityOptions.facilityTypeId = {
    label: 'Facility Type',
    name: 'facilityTypeId',
    type: 'select',
    icon: 'fa-regular fa-building',
    placeholder: 'Facility Type',
    options: facilityTypeOptions,
  };
  facilityOptions.country = {
    label: 'Country',
    name: 'country',
    type: 'select',
    icon: 'fa-solid fa-globe',
    placeholder: 'Country',
    options: countryOptions,
  };
  facilityOptions.postCode = {
    label: 'Post Code',
    name: 'postCode',
    type: 'text',
    icon: 'fa-solid fa-signs-post',
    placeholder: '',
  };
  facilityOptions.externalId = {
    label: 'External ID',
    name: 'externalId',
    type: 'text',
    icon: 'fa fa-cogs',
    placeholder: '',
  };
  facilityOptions.address = {
    label: 'Address',
    name: 'address',
    type: 'text',
    icon: 'fa-solid fa-location-dot',
    placeholder: '',
  };
  facilityOptions.facilityM2 = {
    label: 'Facility m2',
    name: 'facilityM2',
    type: 'number',
    icon: 'fa-solid fa-ruler-combined',
    placeholder: '',
  };
  facilityOptions.isPrimary = {
    label: 'Primary Facility',
    name: 'isPrimary',
    type: 'select',
    icon: 'fa-solid fa-house',
    placeholder: 'Primary Facility',
    options: primaryFacilityOptions,
  };
  facilityOptions.excludeFromReports = {
    label: 'Exclude from Organisation Footprint',
    name: 'excludeFromReports',
    type: 'select',
    icon: 'fa-solid fa-filter-circle-xmark',
    tooltip: {
      show: true,
      content: `Change to 'exclude' if this facility is not part of the company's consumption data and 
      should not be included in the company carbon footprint, 
      for example if this facility and data is a what-if scenario.`,
    },
    placeholder: 'Exclude from Organisation Footprint',
    options: excludeFromReportsOptions,
  };

  return {
    facilities: state?.facility?.list,
    facilityDataModal: state.facilityDataModal,
    facilityDataDeleteModal: state.facility && state.facility.facilityDataDeleteModal,
    branding: state.branding,
    facilityOptions,
    selectedOptions,
    facilitiesPermissions: state?.organisation?.permissions?.facilities,
  };
};

const mapDispatchToProps = (dispatch) => ({
  saveFacility: (facility, history) => dispatch(FACILITYACTIONS.saveFacility(facility, history)),
  showFacilityModal: (status) => dispatch(FACILITYACTIONS.showFacilityModal(status)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Facility);
